import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import emailjs from "@emailjs/browser";
import { FaPaperPlane, FaSchool, FaUser, FaCheckCircle, FaArrowRight } from "react-icons/fa";
import Header from "../components/Header";
import Footer from "../components/Footer";
import "./IncubationRegistration.scss";
import AsOfNote from "../components/AsOfNote";
import { impact } from "../data/stats";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

const RECIPIENT_EMAIL = "cograd.office@gmail.com";
const BOARD_OPTIONS = ["CBSE", "STATE", "ICSE", "Other"];

const BENEFITS = [
  "Structured 12-month incubation roadmap tailored to your school",
  "Access to Medha AI, STEM Labs & digital infrastructure",
  "Dedicated CoGrad advisor + teacher training support",
  "Government-recognised certification for staff",
  "Priority for PM SHRI & KVS partnership programs",
];

const TRUST_STATS = [
  { value: impact.incubated.display, label: impact.incubated.label },
  { value: impact.schools.display,   label: impact.schools.label   },
  { value: impact.students.display,  label: impact.students.label  },
  { value: impact.states.display,    label: impact.states.label    },
];

const IncubationRegistration = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState("");

  const [formData, setFormData] = useState({
    schoolName: "",
    location: "",
    totalStudents: "",
    totalClassrooms: "",
    affiliationValidity: "",
    board: "",
    boardOther: "",
    contactName: "",
    contactNo: "",
    designation: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || "n-n62XZBnXR0TXlQj";
    if (publicKey) emailjs.init(publicKey);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleBoardSelect = (opt) => {
    setFormData((prev) => ({ ...prev, board: opt }));
    if (errors.board) setErrors((prev) => ({ ...prev, board: "" }));
  };

  const validate = () => {
    const next = {};
    if (!formData.schoolName.trim()) next.schoolName = "School name is required";
    if (!formData.location.trim()) next.location = "Location is required";
    if (!formData.totalStudents.trim()) next.totalStudents = "Total students is required";
    if (!formData.totalClassrooms.trim()) next.totalClassrooms = "Total classrooms is required";
    if (!formData.affiliationValidity.trim()) next.affiliationValidity = "Affiliation validity is required";
    if (!formData.board) next.board = "Please select a board";
    if (formData.board === "Other" && !formData.boardOther.trim()) next.boardOther = "Please specify the board";
    if (!formData.contactName.trim()) next.contactName = "Contact person name is required";
    if (!formData.contactNo.trim()) {
      next.contactNo = "Contact number is required";
    } else if (!/^[0-9+\-\s()]{7,20}$/.test(formData.contactNo.trim())) {
      next.contactNo = "Please enter a valid phone number";
    }
    if (!formData.designation.trim()) next.designation = "Designation is required";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!validate()) return;
    setIsLoading(true);

    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID || "service_9wj9fbn";
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || "template_i9cxvxd";
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || "n-n62XZBnXR0TXlQj";

    const boardLabel = formData.board === "Other" ? `Other — ${formData.boardOther}` : formData.board;

    const detailsBlock = [
      `SCHOOL NAME: ${formData.schoolName}`,
      `LOCATION: ${formData.location}`,
      `TOTAL STUDENTS: ${formData.totalStudents}`,
      `TOTAL CLASSROOMS AVAILABLE: ${formData.totalClassrooms}`,
      `AFFILIATION VALIDITY: ${formData.affiliationValidity}`,
      `BOARD: ${boardLabel}`,
      `NAME OF CONTACT PERSON: ${formData.contactName}`,
      `CONTACT NO.: ${formData.contactNo}`,
      `DESIGNATION OF CONTACT PERSON: ${formData.designation}`,
    ].join("\n");

    const messageBody = [
      `New School Incubation Application (Phase-1)`,
      `Submitted via https://cograd.in/incubation/register`,
      `Please forward to ${RECIPIENT_EMAIL}`,
      ``,
      `--- APPLICATION DETAILS ---`,
      detailsBlock,
    ].join("\n");

    const templateParams = {
      from_name: formData.contactName,
      reply_to: RECIPIENT_EMAIL,
      name: formData.contactName,
      email: RECIPIENT_EMAIL,
      phone: formData.contactNo,
      education: formData.schoolName,
      experience: `${boardLabel} | Students: ${formData.totalStudents} | Classrooms: ${formData.totalClassrooms} | Affiliation valid till: ${formData.affiliationValidity}`,
      research: formData.location,
      source: "Cograd Incubation Registration page",
      additional: detailsBlock,
      message: messageBody,
    };

    const saveToDb = async () => {
      const board = formData.board === "Other" ? `Other — ${formData.boardOther}` : formData.board;
      await fetch(`${API_URL}/api/incubation`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          schoolName: formData.schoolName,
          location: formData.location,
          totalStudents: formData.totalStudents,
          totalClassrooms: formData.totalClassrooms,
          affiliationValidity: formData.affiliationValidity,
          board,
          contactName: formData.contactName,
          contactNo: formData.contactNo,
          designation: formData.designation,
        }),
      });
    };

    try {
      // Save to MongoDB — always
      await saveToDb();

      // Try EmailJS — ignore if it fails
      emailjs.send(serviceId, templateId, templateParams, publicKey).catch(() => {});

      setSubmitted(true);
    } catch (err) {
      console.error("Incubation registration submission failed:", err);
      setFormError(`Submission failed. Please try again or write to us at ${RECIPIENT_EMAIL}.`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>School Incubation Application Form (Phase-1) — Cograd</title>
        <meta name="description" content="Apply for the Cograd School Incubation Program. Only schools ready for growth and implementation should apply." />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <Header />

      <main className="incr">

        {/* ── Hero ──────────────────────────────────────────────────────────── */}
        <section className="incr__hero">
          <div className="incr__hero-inner">
            <span className="incr__eyebrow">Cograd Incubation Program · Phase-1</span>
            <h1>School Incubation<br />Application Form</h1>
            <p>Only schools ready for growth and implementation should apply.</p>
          </div>
          <div className="incr__trust-bar">
            {TRUST_STATS.map((s) => (
              <div key={s.label} className="incr__trust-stat">
                <strong>{s.value}</strong>
                <span>{s.label}</span>
              </div>
            ))}
          </div>
          <AsOfNote className="asof--center" />
        </section>

        {/* ── Body ──────────────────────────────────────────────────────────── */}
        <section className="incr__body">
          <div className="incr__layout">

            {/* Left Sidebar */}
            <aside className="incr__sidebar">
              <div className="incr__sidebar-card">
                <p className="incr__sidebar-overline">Why apply?</p>
                <h3 className="incr__sidebar-heading">What you unlock as a CoGrad Incubated School</h3>
                <ul className="incr__benefits">
                  {BENEFITS.map((b) => (
                    <li key={b}>
                      <FaCheckCircle className="incr__benefit-icon" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <a href="/incubation" className="incr__sidebar-link">
                  Learn about the program <FaArrowRight />
                </a>
              </div>

              <div className="incr__sidebar-quote">
                <p>"CoGrad's incubation gave our school a complete roadmap — from labs to teacher training, every step was supported."</p>
                <div className="incr__sidebar-author">
                  <div className="incr__sidebar-avatar">RS</div>
                  <div>
                    <strong>Rajiv Sinha</strong>
                    <span>Principal · Sunrise Public School, UP</span>
                  </div>
                </div>
              </div>
            </aside>

            {/* Form */}
            <div className="incr__form-wrap">
              {!submitted ? (
              <form className="incr__form" onSubmit={handleSubmit} noValidate>

                {/* School Information */}
                <div className="incr__section-head">
                  <span className="incr__section-icon"><FaSchool /></span>
                  <span>School Information</span>
                </div>

                <div className="incr__field">
                  <label htmlFor="schoolName">School Name <span className="incr__req">*</span></label>
                  <input
                    id="schoolName" type="text" name="schoolName"
                    placeholder="Full name of the school"
                    value={formData.schoolName} onChange={handleChange} disabled={isLoading}
                  />
                  {errors.schoolName && <p className="incr__error">{errors.schoolName}</p>}
                </div>

                <div className="incr__field">
                  <label htmlFor="location">School Address <span className="incr__req">*</span></label>
                  <textarea
                    id="location" name="location" rows="3"
                    placeholder="Full address including city, state and PIN code"
                    value={formData.location} onChange={handleChange} disabled={isLoading}
                  />
                  {errors.location && <p className="incr__error">{errors.location}</p>}
                </div>

                <div className="incr__grid-3">
                  <div className="incr__field">
                    <label htmlFor="totalStudents">Total Students <span className="incr__req">*</span></label>
                    <input
                      id="totalStudents" type="text" name="totalStudents"
                      placeholder="e.g. 350"
                      value={formData.totalStudents} onChange={handleChange} disabled={isLoading}
                    />
                    {errors.totalStudents && <p className="incr__error">{errors.totalStudents}</p>}
                  </div>

                  <div className="incr__field">
                    <label htmlFor="totalClassrooms">Classrooms <span className="incr__req">*</span></label>
                    <input
                      id="totalClassrooms" type="text" name="totalClassrooms"
                      placeholder="e.g. 24"
                      value={formData.totalClassrooms} onChange={handleChange} disabled={isLoading}
                    />
                    {errors.totalClassrooms && <p className="incr__error">{errors.totalClassrooms}</p>}
                  </div>

                  <div className="incr__field">
                    <label htmlFor="affiliationValidity">Affiliation Valid Till <span className="incr__req">*</span></label>
                    <input
                      id="affiliationValidity" type="text" name="affiliationValidity"
                      placeholder="e.g. 31/03/2030"
                      value={formData.affiliationValidity} onChange={handleChange} disabled={isLoading}
                    />
                    {errors.affiliationValidity && <p className="incr__error">{errors.affiliationValidity}</p>}
                  </div>
                </div>

                <div className="incr__field">
                  <label>Board <span className="incr__req">*</span></label>
                  <div className="incr__chips">
                    {BOARD_OPTIONS.map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        className={`incr__chip${formData.board === opt ? " incr__chip--active" : ""}`}
                        onClick={() => handleBoardSelect(opt)}
                        disabled={isLoading}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                  {formData.board === "Other" && (
                    <input
                      type="text" name="boardOther" className="incr__chip-input"
                      placeholder="Please specify your board"
                      value={formData.boardOther} onChange={handleChange} disabled={isLoading}
                    />
                  )}
                  {errors.board && <p className="incr__error">{errors.board}</p>}
                  {errors.boardOther && <p className="incr__error">{errors.boardOther}</p>}
                </div>

                {/* Contact Person */}
                <div className="incr__section-head incr__section-head--mt">
                  <span className="incr__section-icon"><FaUser /></span>
                  <span>Contact Person</span>
                </div>

                <div className="incr__grid-2">
                  <div className="incr__field">
                    <label htmlFor="contactName">Full Name <span className="incr__req">*</span></label>
                    <input
                      id="contactName" type="text" name="contactName"
                      placeholder="Contact person's full name"
                      value={formData.contactName} onChange={handleChange} disabled={isLoading}
                    />
                    {errors.contactName && <p className="incr__error">{errors.contactName}</p>}
                  </div>

                  <div className="incr__field">
                    <label htmlFor="contactNo">Mobile Number <span className="incr__req">*</span></label>
                    <input
                      id="contactNo" type="tel" name="contactNo"
                      placeholder="+91 9XXXXXXXXX"
                      value={formData.contactNo} onChange={handleChange} disabled={isLoading}
                    />
                    {errors.contactNo && <p className="incr__error">{errors.contactNo}</p>}
                  </div>
                </div>

                <div className="incr__field">
                  <label htmlFor="designation">Designation <span className="incr__req">*</span></label>
                  <input
                    id="designation" type="text" name="designation"
                    placeholder="e.g. Principal, Director, Trustee"
                    value={formData.designation} onChange={handleChange} disabled={isLoading}
                  />
                  {errors.designation && <p className="incr__error">{errors.designation}</p>}
                </div>

                {formError && <div className="incr__error-banner">{formError}</div>}

                <button type="submit" className="incr__submit" disabled={isLoading}>
                  {isLoading ? "Submitting…" : "Submit Application"}
                  <FaPaperPlane />
                </button>

                <p className="incr__footnote">
                  By submitting, you agree to be contacted by the CoGrad Incubation team at the details you provided.
                </p>

              </form>
            ) : (
              <div className="incr__submitted">
                <div className="incr__submitted-icon">✓</div>
                <h3>Application Submitted!</h3>
                <p>Our Incubation team will review your application and reach out within 2–3 working days.</p>
                <a href="/incubation" className="incr__submitted-link">
                  Back to Incubation Program <FaArrowRight />
                </a>
              </div>
            )}
            </div>

          </div>
        </section>

      </main>

      <Footer />
    </>
  );
};

export default IncubationRegistration;
